import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { captureChatOwner } from './creation-actions';
import { sessionRevision, sessionScope } from '@/features/auth/session-scope';
import { useAuthStore } from '@/store/auth-store';
import type { ChannelMessage, ChannelMessagesInfiniteData, ChatThreadPage, ChatThreadData } from './queries';

export type MessageOwner = ReturnType<typeof captureChatOwner>;
export type MessageCacheKind = 'messages' | 'thread' | 'pinned' | 'search' | 'threads' | 'tracked-threads';
export function messageKey(kind: MessageCacheKind, resource = '', owner = captureChatOwner()) {
  return ['team-chat', kind, resource, 'owner', owner.scope, owner.revision] as const;
}
export function isCurrentMessageOwner(owner: MessageOwner) {
  return owner.revision === sessionRevision() && owner.scope === sessionScope(useAuthStore.getState());
}
export function ownsMessageKey(key: QueryKey, owner = captureChatOwner(), kind?: MessageCacheKind, resource?: string) {
  return key[0] === 'team-chat' && ['messages','thread','pinned','search','threads','tracked-threads'].includes(String(key[1])) &&
    key[3] === 'owner' && key[4] === owner.scope && key[5] === owner.revision &&
    (kind === undefined || key[1] === kind) && (resource === undefined || key[2] === resource);
}
/** Only captured current-owner caches may receive a validated HTTP row. */
export function patchChannelMessagesCache(qc: QueryClient, channelId: string, updater: (rows: ChannelMessage[]) => ChannelMessage[], owner = captureChatOwner()) {
  if (!isCurrentMessageOwner(owner)) return;
  qc.setQueryData<ChannelMessage[]>(messageKey('messages', channelId, owner), prev => Array.isArray(prev) ? updater(prev) : prev);
  qc.setQueryData<ChannelMessagesInfiniteData>([...messageKey('messages', channelId, owner), 'infinite'], prev => {
    if (!prev) return prev;
    return {...prev, pages: prev.pages.map((page, index) => {
      const next = updater(page);
      if (index === 0 || next === page) return next;
      const ids = new Set(page.map(row => row.id));
      return next.filter(row => ids.has(row.id));
    })};
  });
}
export function reconcileChatMessages(qc: QueryClient, channelId?: string, parentId?: string, owner = captureChatOwner()) {
  if (!isCurrentMessageOwner(owner)) return Promise.resolve();
  return qc.invalidateQueries({predicate: query => ownsMessageKey(query.queryKey, owner) &&
    (channelId === undefined || query.queryKey[1] === 'tracked-threads' || query.queryKey[1] === 'thread' && (parentId === undefined || query.queryKey[2] === parentId) || query.queryKey[2] === channelId)});
}

/** Preserve loaded older reply pages; only the newest page may append an ACK. */
export function patchThreadCache(qc:QueryClient,parentId:string,updater:(page:ChatThreadPage,append:boolean)=>ChatThreadPage|undefined,owner=captureChatOwner()){
 if(!isCurrentMessageOwner(owner))return;
 const key=messageKey('thread',parentId,owner);
 qc.setQueryData<ChatThreadPage>(key,prev=>prev?updater(prev,true):prev);
 qc.setQueryData<ChatThreadData>([...key,'infinite'],prev=>{
  if(!prev)return prev;
  const pages=prev.pages.map((page,index)=>updater(page,index===0));
  if(pages.some(page=>page===undefined))return undefined;
  return {...prev,pages:pages as ChatThreadPage[]};
 });
}
